<template>
  <v-container fluid class="customer-list-page pa-0">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />

    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editCustomer="selectedCustomer"
    />

    <v-card :loading="loading" outlined class="customer-list-card">
      <div class="customer-list-header">
        <div>
          <div class="page-title">Customer List</div>
          <div class="page-subtitle">
            {{ customers.length }} customer{{ customers.length === 1 ? "" : "s" }} found
          </div>
        </div>

        <v-btn
          v-if="checkRightStatus(7)"
          small
          rounded
          color="primary"
          dark
          @click="add"
        >
          <v-icon left small>mdi-plus</v-icon>
          New Customer
        </v-btn>
      </div>

      <div class="customer-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Business, contact, phone, address"
          single-line
          hide-details
          clearable
          class="customer-search"
        />
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <div class="summary-value">{{ total | currency }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Total</div>
            <div class="summary-value">{{ fullTotal | currency }}</div>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="customers"
          :search="search"
          :loading="loading"
          :sort-desc="sortDesc"
          @current-items="updateTotal"
          :sort-by="sortBy"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
          class="customer-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer customer-row">
              <td class="text-left" @click="viewProfile(item)">
                <div
                  class="font-weight-bold"
                  v-html="$highlight(item.name || '', safeSearch)"
                >
                  {{ item.name }}
                </div>
                <div class="caption grey--text">
                  {{ item.email || "" }}
                </div>
              </td>

              <td
                class="text-left"
                @click="viewProfile(item)"
                v-html="$highlight(item.contactName || '', safeSearch)"
              >
                {{ item.contactName }}
              </td>

              <td class="text-left" @click="viewProfile(item)">
                <div
                  v-if="item.cellNumber"
                  v-html="$highlight(item.cellNumber || '', safeSearch)"
                >
                  {{ item.cellNumber }}
                </div>
                <div v-else class="grey--text">Not Set</div>
              </td>

              <td class="text-left" @click="viewProfile(item)">
                <div
                  v-if="item.address"
                  v-html="$highlight(item.address || '', safeSearch)"
                >
                  {{ item.address }}
                </div>
                <div class="caption grey--text">
                  {{ item.city || "" }}
                  <span v-if="item.city && item.state">,</span>
                  {{ item.state || "" }}
                </div>
              </td>

              <td class="text-right font-weight-bold" @click="viewProfile(item)">
                <span v-if="checkRightStatus(33)">
                  {{ item.balance | currency }}
                </span>
              </td>

              <td class="text-right" @click="viewProfile(item)">
                <span v-if="checkRightStatus(33)">
                  {{ item.balanceLimit | currency }}
                </span>
              </td>

              <td class="text-right" @click="viewProfile(item)">
                <span
                  v-if="checkRightStatus(33)"
                  :class="balanceDiff(item) < 0 ? 'negative-value' : 'positive-value'"
                >
                  {{ balanceDiff(item) | currency }}
                </span>
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item
                      v-if="checkRightStatus(23)"
                      @click="editCustomer(item)"
                    >
                      <v-list-item-title>
                        {{ $t("buttons.edit") }}
                      </v-list-item-title>
                    </v-list-item>

                    <DeleteCustomer :Customer="item" @submit="getList()" />
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>

          <template v-slot:body.append v-if="customers.length > 0">
            <tr class="table-total-row">
              <td colspan="4" class="text-right font-weight-bold">
                List Total
              </td>

              <td class="text-right font-weight-bold">
                {{ total | currency }}
              </td>

              <td colspan="3"></td>
            </tr>

            <tr class="table-result-row">
              <td colspan="4" class="text-right font-weight-bold">
                Result Total
              </td>

              <td class="text-right font-weight-bold">
                {{ fullTotal | currency }}
              </td>

              <td colspan="3"></td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import customerService from "../service.js";
import DeleteCustomer from "../components/DeleteCustomer";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "customer-list",

  components: {
    Create,
    Edit,
    DeleteCustomer,
  },

  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedCustomer: null,
      total: 0,
      fullTotal: 0,
      headers: [
        {
          text: "Business Name",
          value: "name",
          sortable: true,
          align: "left",
        },
        {
          text: "Contact Name",
          value: "contactName",
          align: "left",
          sortable: true,
        },
        {
          text: "Cell Number",
          value: "cellNumber",
          align: "left",
          sortable: true,
        },
        {
          text: "Address",
          value: "address",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("labels.balance"),
          value: "balance",
          align: "right",
          sortable: true,
        },
        {
          text: "Limit",
          value: "balanceLimit",
          align: "right",
          sortable: true,
        },
        {
          text: "Available",
          value: "diff",
          align: "right",
          sortable: false,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      page: 1,
      sortBy: "balance",
      pageCount: 0,
      sortDesc: true,
      itemsPerPage: 10,
      search: "",
      customers: [],
      loading: true,
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    safeSearch() {
      return (this.search || "").toLowerCase();
    },
  },

  created() {
    this.getList();
    this.$events.listen("customerListEvent", () => this.getList());
  },

  beforeDestroy() {
    this.$events.remove("customerListEvent");
  },

  methods: {
    balanceDiff(customer) {
      return Number(customer.balanceLimit || 0) - Number(customer.balance || 0);
    },

    updateTotal(val) {
      this.total = val.reduce((sum, row) => {
        return sum + Number(row.balance || 0);
      }, 0);
    },

    viewProfile(customer) {
      this.$router.push({ path: `/customer/${customer.id}/profile` });
    },

    add() {
      this.createDialog = true;
    },

    async getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      try {
        const where = {};

        if (!this.currentUser.isAdmin && this.checkRightStatus(50)) {
          where.SalesRepUserId = this.currentUser.id;
        }

        const response = await customerService.getAll(where);

        this.customers = response.data || [];

        this.fullTotal = this.customers.reduce((sum, row) => {
          return sum + Number(row.balance || 0);
        }, 0);

        return response;
      } catch (error) {
        console.error("customer getList failed", error);
        this.customers = [];
        this.total = 0;
        this.fullTotal = 0;
      } finally {
        this.loading = false;
      }
    },

    editCustomer(customer) {
      this.selectedCustomer = customer;
      this.editDialog = true;
    },
  },
};
</script>

<style scoped>
.customer-list-page {
  background: #f8fafc;
}

.customer-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.customer-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.customer-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.customer-search {
  max-width: 420px;
  min-width: 280px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.customer-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.customer-row:hover {
  background: #f8fafc;
}

.positive-value {
  color: #16a34a;
  font-weight: 700;
}

.negative-value {
  color: #dc2626;
  font-weight: 700;
}

.table-total-row {
  background: #f8fafc;
}

.table-result-row {
  background: #eef2ff;
}

@media (max-width: 600px) {
  .customer-search {
    max-width: 100%;
    min-width: 100%;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }
}
</style>